<template>
  <div>
    <hr :class="$style.divider">
    <div
      v-for="(i, parent_index) in osRestrictions"
      :key="parent_index"
    >
      <HeadingSmall>{{ getOsName(osRestrictions[parent_index].osName) }}</HeadingSmall>
      <div
        v-if="showVersions(parent_index)"
        data-test-id="PolicyOsRestrictions_showVersions"
      >
        <HeadingExtraSmall>Minimum Supported Version</HeadingExtraSmall>
        <p>
          {{ getOsName(osRestrictions[parent_index].osName) }}
          {{ osRestrictions[parent_index].earliestVersion }}
          or later.
        </p>
        <HeadingExtraSmall v-if="showDeprecated(parent_index)">
          Deprecated Versions
        </HeadingExtraSmall>
        <p
          v-if="showDeprecated(parent_index)"
          data-test-id="PolicyOsRestrictions_showDeprecatedParagraph"
        >
          {{ osRestrictions[parent_index].deprecatedVersion }}
          or later.
        </p>
      </div>
      <div
        v-if="showSupportedEnrollmentTypes(parent_index)"
        :class="$style.listContainer"
      >
        <span :class="$style.enrollmentTypes">
          <HeadingExtraSmall>Supported Enrollment Types</HeadingExtraSmall>
          <IconTooltip :iconClass="['jc-info-circle']">
            <p>
              <strong>User-Enrolled Devices</strong> -
              Devices are owned by the user and enrolled by the user.
            </p>
            <p>
              <strong>Device-Enrolled Devices</strong> -
              Devices are owned by the corporation, and enrolled by the admin or by the user.
            </p>
            <p>
              <strong>Auto-Enrolled Devices</strong> -
              Devices are owned and enrolled by the corporation
              via Automated Device Enrollment.
            </p>
          </IconTooltip>
        </span>
        <ul
          v-for="enrollmentType in enrollmentTypes"
          :key="enrollmentType"
          :class="$style.list"
        >
          <li
            v-if="isEnrollmentTypeSupported(enrollmentType, parent_index)
              || allTypesAreSupported(parent_index)"
          >
            <Icon
              data-test-id="PolicyOSRestrictions_supported"
              icon="checkmark"
              inline
            /> {{ formatEnrollmentType(enrollmentType) }}
          </li>
          <li
            v-else
            :class="$style.notSupported"
          >
            <Icon
              data-test-id="PolicyOsRestrictions_notSupported"
              icon="close"
              inline
            /> {{ formatEnrollmentType(enrollmentType) }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import { HeadingExtraSmall, HeadingSmall, Icon } from '@jumpcloud/ui-components';
import { capitalize, get, isEmpty } from 'lodash';
import Constants, {
  PolicyConsts,
} from 'client/util/Constants';
import IconTooltip from 'client/components/Tooltip/IconTooltip.vue';

const {
  ios,
} = PolicyConsts.policyTemplateTypes;
const { darwin } = Constants;

export default {
  name: 'PolicyOsRestrictions',

  components: {
    HeadingExtraSmall,
    HeadingSmall,
    Icon,
    IconTooltip,
  },

  props: {
    osMetaFamily: {
      type: String,
      required: true,
    },
    osRestrictions: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      ios,
      enrollmentTypes: ['user', 'device', 'automated'],
    };
  },

  methods: {
    allTypesAreSupported(index) {
      // if this.osRestrictions[index].appleRestrictions.supportedEnrollmentTypes is an empty array
      // but it's an iOS or iPadOS policy, the backend
      // treats this as all enrollment types as supported.
      // Therefore, I render all enrollment types as checkmarks
      const supportedEnrollmentTypes = get(this.osRestrictions[index], 'appleRestrictions.supportedEnrollmentTypes');
      const areAllEnrollmentTypesSupported = supportedEnrollmentTypes
      && isEmpty(supportedEnrollmentTypes);
      const isIosPolicy = this.osMetaFamily === ios;

      return areAllEnrollmentTypesSupported && isIosPolicy;
    },

    formatEnrollmentType(enrollmentType) {
      if (enrollmentType === 'automated') {
        return 'Auto-Enrolled Devices';
      }
      return `${capitalize(enrollmentType)}-Enrolled Devices`;
    },

    getOsName(name) {
      return name === darwin ? 'macOS' : name;
    },

    isEnrollmentTypeSupported(enrollmentType, index) {
      return this.osRestrictions[index].appleRestrictions
        .supportedEnrollmentTypes.indexOf(enrollmentType) >= 0;
    },

    showDeprecated(index) {
      return !isEmpty(this.osRestrictions[index].deprecatedVersion);
    },

    showSupportedEnrollmentTypes(index) {
      return this.osRestrictions[index].appleRestrictions
      && this.osRestrictions[index].appleRestrictions.supportedEnrollmentTypes;
    },

    showVersions(index) {
      return !isEmpty(this.osRestrictions[index])
      && this.osRestrictions[index].earliestVersion;
    },
  },
};

</script>
<style module>
@import '../components/FormValidation/css/formFields.css';

.divider {
  margin-bottom: 2rem;
}

.enrollmentTypes {
  display: inline-flex;
}

.list {
  list-style: none;
  margin: 0.6rem auto 0;
  padding-left: 0;
}

.listContainer {
  margin-bottom: 2rem;
}

.notSupported {
  opacity: 0.5;
}
</style>
